import { Modal, Button, Spinner, Table, Collapse } from "react-bootstrap";
import "./CustomCostModel.scss";

const TAB_ORDER = ["module", "material", "labour"];

const TAB_CONFIG = {
  module: { label: "Module Rates", icon: "fa-cogs" },
  material: { label: "Material Rates", icon: "fa-cubes" },
  labour: { label: "Labour Rates", icon: "fa-users" },
};

export default function CustomCostModal({
  show,
  onHide,
  tabs,
  activeTab,
  setActiveTab,
  formatCurrency,
  formatExactCurrency,
  onSaveAll,
  onClose,
  showToast,
}) {
  const tab = tabs[activeTab];
  const {
    config,
    data,
    customSummary,
    rates,
    customRates,
    rateInputStrings,
    expandedFloor,
    setExpandedFloor,
    error,
    setError,
    saving,
    handleRateChange,
    handleSave,
  } = tab || {};

  const {
    title,
    icon,
    emptyMessage,
    getFloors,
    getModules,
    getModuleItems,
    getItemName,
    getItemQty,
    getItemUnit,
    getCurrentRate,
    getCustomRate,
    getAmount,
    hasCustomRate,
    itemLabel,
    getGrandTotal,
  } = config || {};

  // Helper to call config functions with rates/customRates context
  const callConfigFn = (fn, item) => fn(item, rates, customRates);

  const isAnySaving = TAB_ORDER.some((t) => tabs[t]?.saving);

  // Prefer exact formatter; fall back to rounded if parent didn't pass one
  const money = formatExactCurrency || formatCurrency;

  return (
    <Modal
      show={show}
      onHide={onClose || onHide}
      size="xl"
      className="project-settings-modal cost-modal"
      centered
      backdrop="static"
      keyboard={true}
    >
      <Modal.Header closeButton>
        <div className="cost-modal-heading">
          <div className="cost-modal-title-wrap">
            <span className="cost-modal-title-icon">
              <i className={`fa ${icon}`}></i>
            </span>
            <div>
              <Modal.Title>{title}</Modal.Title>
              <p>Manage module rates and configuration for your project</p>
            </div>
          </div>
          <div className="settings-tab-nav">
            {TAB_ORDER.map((tabKey) => {
              const tabInfo = TAB_CONFIG[tabKey];
              const tabData = tabs[tabKey];
              const isActive = activeTab === tabKey;
              const isDisabled =
                !tabData || !tabData.data || tabData.data.length === 0;
              return (
                <button
                  key={tabKey}
                  type="button"
                  className={`settings-tab-btn ${isActive ? "active" : ""}`}
                  onClick={() => !isDisabled && setActiveTab(tabKey)}
                  disabled={isDisabled}
                  title={isDisabled ? "No data available" : ""}
                >
                  <i className={`fa ${tabInfo.icon}`}></i>
                  {tabInfo.label}
                </button>
              );
            })}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="cost-modal-body">
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show mb-3"
            role="alert"
          >
            <i className="fa fa-exclamation-triangle mr-2"></i>
            {error}
            <button
              type="button"
              className="close"
              onClick={() => setError(null)}
            >
              <span>&times;</span>
            </button>
          </div>
        )}
        {!data || data.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="purple" />
            <p className="text-muted mt-2">{emptyMessage}</p>
          </div>
        ) : (
          <>
            <div className="cost-info-banner">
              <div className="cost-info-icon">
                <i className="fa fa-info"></i>
              </div>
              <div className="cost-info-copy">
                <span>
                  Edit the <strong>Custom Rate</strong> column to override
                  default rates. Amounts recalculate automatically. Click{" "}
                  <strong>Save All</strong> to persist and re-run analysis.
                </span>
              </div>
              <div className="cost-info-total">
                Total:{" "}
                <strong>
                  {money(
                    customSummary && getGrandTotal
                      ? getGrandTotal(customSummary)
                      : 0,
                  )}
                </strong>
              </div>
            </div>
            <div className="cost-floor-list">
              {getFloors(data, rates, customRates).map((floor, floorIdx) => (
                <div key={floorIdx} className="settings-floor-card">
                  <div
                    className="settings-floor-header"
                    onClick={() =>
                      setExpandedFloor(
                        expandedFloor === floor.name ? null : floor.name,
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div className="cost-floor-icon">
                      <i
                        className={`fa ${expandedFloor === floor.name ? "fa-layer-group" : "fa-home"}`}
                      ></i>
                    </div>
                    <div className="cost-floor-name">
                      <h6>{floor.name}</h6>
                      <span>{getModules(floor).length} modules</span>
                    </div>
                    <div className="cost-floor-total">
                      <span>Floor Total</span>
                      <strong>{money(floor.totalAmount || 0)}</strong>
                    </div>
                    <i
                      className={`cost-floor-chevron fa fa-chevron-right ${
                        expandedFloor === floor.name ? "is-open" : ""
                      }`}
                    ></i>
                  </div>
                  <Collapse in={expandedFloor === floor.name} timeout={250}>
                    <div className="cost-floor-content">
                      {getModules(floor).map((module, moduleIdx) => (
                        <div
                          key={moduleIdx}
                          className="settings-module-card card mb-3"
                        >
                          <div className="card-header">
                            <span>
                              <i className="fa fa-cube"></i>
                              {module.displayName}
                            </span>
                            <span className="cost-item-count">
                              {getModuleItems(module).length} items
                            </span>
                          </div>
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <Table className="settings-rate-table mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ width: "30%" }}>
                                      {itemLabel}
                                    </th>
                                    <th
                                      className="text-center"
                                      style={{ width: "8%" }}
                                    >
                                      Unit
                                    </th>
                                    <th
                                      className="text-right"
                                      style={{ width: "12%" }}
                                    >
                                      Qty
                                    </th>
                                    <th
                                      className="text-right"
                                      style={{ width: "14%" }}
                                    >
                                      Current
                                    </th>
                                    <th
                                      className="text-right"
                                      style={{ width: "14%" }}
                                    >
                                      Custom
                                    </th>
                                    <th
                                      className="text-right"
                                      style={{ width: "14%" }}
                                    >
                                      Amount
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getModuleItems(module).map(
                                    (item, itemIdx) => {
                                      const itemName = getItemName(item);
                                      const isCustom = callConfigFn(
                                        hasCustomRate,
                                        item,
                                      );
                                      return (
                                        <tr
                                          key={itemIdx}
                                          className={
                                            isCustom ? "rate-changed" : ""
                                          }
                                        >
                                          <td className="font-weight-bold text-dark">
                                            {itemName}
                                          </td>
                                          <td className="text-center align-middle">
                                            <span className="cost-unit-badge">
                                              {getItemUnit(item)}
                                            </span>
                                          </td>
                                          <td className="text-right align-middle font-weight-bold text-dark">
                                            {getItemQty(
                                              item,
                                            )?.toLocaleString() || "-"}
                                          </td>
                                          <td className="text-right align-middle text-muted">
                                            {money(
                                              callConfigFn(
                                                getCurrentRate,
                                                item,
                                              ),
                                            )}
                                          </td>
                                          <td className="text-right align-middle">
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              autoComplete="off"
                                              className="form-control form-control-sm settings-rate-input"
                                              value={
                                                rateInputStrings[itemName] || ""
                                              }
                                              onChange={(e) => {
                                                const cleaned = e.target.value
                                                  .replace(/[^0-9.]/g, "")
                                                  .replace(/(\..*)\./g, "$1");
                                                handleRateChange(
                                                  itemName,
                                                  cleaned,
                                                );
                                              }}
                                              onWheel={(e) =>
                                                e.currentTarget.blur()
                                              }
                                              placeholder="Override"
                                              style={{ width: "100%" }}
                                            />
                                          </td>
                                          <td className="text-right align-middle font-weight-bold text-dark">
                                            {money(
                                              callConfigFn(getAmount, item),
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    },
                                  )}
                                </tbody>
                                <tfoot>
                                  <tr className="font-weight-bold bg-light">
                                    <th colSpan="5" className="text-right">
                                      Module Total
                                    </th>
                                    <th className="text-right">
                                      {money(module.totalAmount || 0)}
                                    </th>
                                  </tr>
                                </tfoot>
                              </Table>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              ))}
            </div>
             
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="cost-modal-footer">
        <Button
          className="cost-cancel-btn"
          onClick={onClose || onHide}
          disabled={isAnySaving}
        >
          <i className="fa fa-times"></i>Cancel
        </Button>
        <Button
          className="cost-save-btn"
          onClick={onSaveAll}
          disabled={isAnySaving}
        >
          {isAnySaving ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Saving All...
            </>
          ) : (
            <>
              <i className="fa fa-save mr-2"></i>Save All Rates
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
